import React, { useState, useEffect, useRef } from "react";
import { ResizableBox } from "react-resizable";
import "./ScheduleModal/SidebarModal.css";
import * as Icon from "react-feather";
import { Form, Accordion, Collapse } from "react-bootstrap";
import { openGitHubPullRequestService, getRepoConfigService, fetchBranchesService, syncBranchesFromGitHubService } from "../services/editor";
import ByPassTestsModal from "./Modals/ByPassTests";
import { CircularProgress, Button } from "@mui/material"
import DbtProfileDropdown from "./ScheduleModal/DbtProfileDropdown";
import { useEditorV2 } from "../contexts/EditorV2Context";

export default function PullRequestModalV2({
  setIsShown,
  activeTenant,
  test_dbt_model,
  dbt_test_status,
  dbt_logs,
  dbt_url,
  dbtProfile,
  dbtProfiles,
  setDbtProfile
}) {
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [dbtRepoName, setDbtRepoName] = useState(null);

  // Dark mode detection - only apply when vscode iframe is open
  const { activeVSCode } = useEditorV2();
  const isDarkMode = !!activeVSCode;

  // Branch selection state
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [isBranchesLoading, setIsBranchesLoading] = useState(false);
  const [isBranchMenuOpen, setIsBranchMenuOpen] = useState(false);
  const [branchSearchTerm, setBranchSearchTerm] = useState('');
  const branchMenuRef = useRef(null);

  // Fetch branches on mount
  useEffect(() => {
    const fetchBranches = async () => {
      setIsBranchesLoading(true);
      try {
        // Sync from GitHub first to get fresh branches
        await syncBranchesFromGitHubService();

        // Then fetch the updated branch list
        const response = await fetchBranchesService();
        if (response?.data?.branches) {
          setBranches(response.data.branches);
          // Default to first non-main branch, or empty if none
          const nonMainBranch = response.data.branches.find(b => b.branch_name !== 'main');
          setSelectedBranch(nonMainBranch?.branch_name || '');
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
      setIsBranchesLoading(false);
    };
    fetchBranches();
  }, []);

  // Fetch repository configuration on mount
  useEffect(() => {
    const fetchRepoConfig = async () => {
      try {
        const response = await getRepoConfigService();
        if (response?.data?.dbt_repo) {
          setDbtRepoName(response.data.dbt_repo);
        } else {
          // Fallback to tenant name if API fails
          const tenantName = typeof activeTenant === 'string'
            ? activeTenant
            : activeTenant?.name || 'default';
          setDbtRepoName(tenantName);
        }
      } catch (error) {
        console.error('Error fetching repo config:', error);
        const tenantName = typeof activeTenant === 'string'
          ? activeTenant
          : activeTenant?.name || 'default';
        setDbtRepoName(tenantName);
      }
    };
    fetchRepoConfig();
  }, [activeTenant]);

  // Click outside to close branch menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (branchMenuRef.current && !branchMenuRef.current.contains(event.target)) {
        setIsBranchMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter branches based on search term
  const filteredBranches = branches
    .filter(b => b.branch_name !== 'main')
    .filter(b => b.branch_name.toLowerCase().includes(branchSearchTerm.toLowerCase()));

  const selectBranch = (branchName) => {
    setSelectedBranch(branchName);
    setIsBranchMenuOpen(false);
    setBranchSearchTerm('');
  };

  // Construct dbt command from selected branch
  const dbtCommand = selectedBranch ? `dbt build --select state:modified` : '';

  const handleCreatePullRequest = () => {
    if (dbt_test_status === "failed" || dbt_test_status === "testing" || (!dbt_test_status && dbt_logs)) {
      setShowBypassModal(true);
    } else {
      openGitHubPullRequest();
    }
  };

  const openGitHubPullRequest = async () => {
    if (dbt_test_status === "testing") await openGitHubPullRequestService(dbt_url);

    // Use dbtRepoName if available, otherwise fallback to tenantName
    const repoName = dbtRepoName || (typeof activeTenant === 'string' ? activeTenant : activeTenant?.name || 'default');
    const pullRequestUrl = `https://github.com/datajolt/${repoName}/compare/main...${selectedBranch}`;
    window.open(pullRequestUrl, "_blank");
  };

  const handleTestClick = () => {
    test_dbt_model(dbtCommand, selectedBranch);
  };

  const getStatusIcon = (isButton = false) => {
    if (dbt_test_status === "testing" || (!dbt_test_status && dbt_logs)) {
      return (
        <CircularProgress size={16} color="#fff" />
      );
    } else if (dbt_test_status === "success") {
      return <Icon.Check color={isButton ? "white" : "green"} />;
    } else if (dbt_test_status === "failed") {
      return <Icon.X color={isButton ? "white" : "red"} />;
    }
    return null;
  };

  return (
    <>
      <ResizableBox
        className={`dragged-box react-resizable sidebar-modal-wrapper pull-request-modal from-v2 ${isDarkMode ? 'dark-theme' : ''}`}
        height={window.innerHeight}
        width={window.innerWidth / 3}
        maxConstraints={[window.innerWidth - 65, window.innerHeight]}
        minConstraints={[400, 400]}
        resizeHandles={["w"]}
        onResizeStart={() => document.body.classList.add('sidebar-resizing')}
        onResizeStop={() => document.body.classList.remove('sidebar-resizing')}
        id="active-modal-sidebar"
        style={{
          zIndex: 1050,
          position: 'relative',
          top: 0,
          border: isDarkMode ? '1.2px solid #3c3c3c' : '1.2px solid #dfe0eb',
          borderRadius: 0,
          boxShadow: 'none'
        }}
      >
        <section className="schedule-sidebar-content" style={{ padding: 0, backgroundColor: isDarkMode ? '#1e1e1e' : 'white' }}>
          {/* V2 Header - matches Create DAG styling */}
          <h2 className="heading-schedule-model m-0">
            <small
              style={{
                fontWeight: 500,
                fontSize: '12px',
                border: 0,
                borderRadius: 0,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px',
                padding: '16px 15px',
                height: 'auto',
                boxShadow: 'none',
                color: isDarkMode ? '#ffffff' : '#448593',
                borderBottom: isDarkMode ? '1.2px solid #3c3c3c' : '1.2px solid #dfe0eb',
                backgroundColor: isDarkMode ? '#1e1e1e' : 'transparent'
              }}
            >
              <Icon.ChevronsRight
                color={isDarkMode ? '#ffffff' : '#438593'}
                role="button"
                onClick={() => setIsShown(false)}
                size={18}
                style={{ cursor: 'pointer' }}
              />
              Create Pull Request
            </small>
          </h2>

          {/* V2 Content wrapper */}
          <div className="v2-content" style={{ padding: '0', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', backgroundColor: isDarkMode ? '#1e1e1e' : 'transparent' }}>
            {/* Scrollable content area */}
            <div style={{ padding: '30px', paddingBottom: '15px', overflow: 'auto', flex: 1, backgroundColor: isDarkMode ? '#1e1e1e' : 'transparent' }}>
            {/* Branch & Test Command Box - matches DAG alias/Schedule Frequency style */}
            <Accordion defaultActiveKey="1" className="pl-0 mb-3">
              <Accordion.Item
                eventKey="1"
                className="bg-white"
                style={{
                  borderRadius: '18px',
                  background: 'linear-gradient(to bottom, #fff, #f9fbfe)',
                  boxShadow: '0 8px 20px rgba(85, 110, 230, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden',
                  border: 'none',
                  transition: 'all 0.05s ease-in-out'
                }}
              >
                <Accordion.Body
                  className="animate__animated animate__fadeIn"
                  style={{
                    padding: '18px 25px 22px',
                    backgroundColor: 'rgba(85, 110, 230, 0.03)',
                    borderTop: '1px solid rgba(85, 110, 230, 0.05)',
                    borderRadius: '18px',
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  {/* Branch Selection */}
                  <Form.Group controlId="branchSelect" className="mb-3">
                    <Form.Label
                      style={{
                        fontSize: '11px',
                        fontWeight: '500',
                        color: '#495057',
                      }}
                    >
                      Select Branch
                    </Form.Label>
                    <div style={{ position: 'relative' }} ref={branchMenuRef}>
                      <Button
                        onClick={() => !isBranchesLoading && setIsBranchMenuOpen(!isBranchMenuOpen)}
                        disabled={isBranchesLoading}
                        sx={{
                          width: "100%",
                          justifyContent: 'space-between',
                          fontSize: "11px",
                          bgcolor: "#fff",
                          color: selectedBranch ? "#495057" : "grey",
                          border: "1px solid #ebebeb",
                          height: "36px",
                          textTransform: 'none',
                          "&:hover": {
                            bgcolor: "#ebebeb",
                            color: "#000",
                            borderColor: "#3266bb"
                          }
                        }}
                      >
                        {isBranchesLoading ? <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><CircularProgress size={12} /> Loading branches...</span> : (selectedBranch || 'Select branch')}
                        <Icon.ChevronDown size={14} style={{ marginLeft: '8px' }} />
                      </Button>
                      <Collapse in={isBranchMenuOpen}>
                        <ul className="schedule-menu" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                          {/* Search input */}
                          <li style={{ padding: 0, borderBottom: '1px solid #ebebeb' }}>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                              <Icon.Search size={14} color="#6c757d" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                              <input
                                type="text"
                                placeholder="Search branches..."
                                value={branchSearchTerm}
                                onChange={(e) => setBranchSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: '100%',
                                  padding: '10px 12px 10px 34px',
                                  border: 'none',
                                  fontSize: '11px',
                                  fontFamily: "'Poppins', sans-serif",
                                  outline: 'none',
                                }}
                                autoFocus
                              />
                            </div>
                          </li>
                          {filteredBranches.length === 0 ? (
                            <li
                              style={{
                                padding: '10px 12px',
                                color: '#6c757d',
                                fontSize: '11px',
                                fontFamily: "'Poppins', sans-serif",
                              }}
                            >
                              No branch found
                            </li>
                          ) : (
                            filteredBranches.map(branch => (
                              <li
                                key={branch.branch_name}
                                onClick={() => selectBranch(branch.branch_name)}
                                className="schedule-menu-item"
                                style={{
                                  padding: '10px 12px',
                                  fontSize: '11px',
                                  fontFamily: "'Poppins', sans-serif",
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <Icon.GitBranch size={14} color="#6c757d" />
                                {branch.branch_name}
                              </li>
                            ))
                          )}
                        </ul>
                      </Collapse>
                    </div>
                  </Form.Group>

                  <Form.Group controlId="dependencies">
                    <Form.Label
                      style={{
                        fontSize: '11px',
                        fontWeight: '500',
                        color: '#495057',
                      }}
                    >
                      Testing your branch with the following command
                    </Form.Label>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        value={dbtCommand}
                        type="text"
                        style={{fontSize: "11px"}}
                        disabled
                      />
                      <DbtProfileDropdown
                        dbtProfile={dbtProfile}
                        dbtProfiles={dbtProfiles}
                        setDbtProfile={setDbtProfile}
                        className="test-button p-0 border border-white"
                        sx={{
                          height: "34px",
                        }}
                        darkMode={isDarkMode}
                      />
                      <Button
                        variant="contained"
                        onClick={handleTestClick}
                        disabled={!selectedBranch || dbt_test_status === "testing" || (!dbt_test_status && dbt_logs)}
                        sx={{
                          height: "32px",
                          backgroundColor: "#3F80EA",
                          color: "#fff",
                          fontSize: "11px",
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          borderTopRightRadius: '8px',
                          borderBottomRightRadius: '8px',
                          "&:disabled": {
                            backgroundColor: "#3F80EA",
                            color: "#fff"
                          }
                        }}
                      >
                        <Icon.Code size={16} style={{ flexShrink: 0 }} />&nbsp;
                        {dbt_test_status === "testing" || (!dbt_test_status && dbt_logs) ? "Testing..." : "Test"}
                      </Button>
                    </div>
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            <Form
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Accordion
                defaultActiveKey="0"
                className="pl-0"
                style={{ flex: 1 }}
              >
                <Accordion.Item
                  eventKey="0"
                  className="bg-white"
                  style={{
                    borderRadius: '18px',
                    background: 'linear-gradient(to bottom, #fff, #f9fbfe)',
                    boxShadow: '0 8px 20px rgba(85, 110, 230, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
                    overflow: 'hidden',
                    border: 'none',
                    transition: 'all 0.05s ease-in-out',
                    height: "100%"
                  }}
                >
                  <Accordion.Header
                    style={{
                      background: 'transparent',
                      border: 'none',
                      width: '100%',
                      borderRadius: '18px 18px 0 0',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div
                      className="d-flex align-items-center w-100"
                      style={{
                        fontWeight: '600',
                        fontSize: '12px',
                        color: '#556ee6',
                        letterSpacing: '0.2px'
                      }}
                    >
                      {/* Conditional icon container based on status */}
                      {dbt_test_status === "success" ? (
                        <div style={{
                          backgroundColor: 'rgba(46, 202, 106, 0.15)',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '12px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 5px rgba(46, 202, 106, 0.2)'
                        }}>
                          <Icon.Check color="#2eca6a" size={16} />
                        </div>
                      ) : dbt_test_status === "failed" ? (
                        <div style={{
                          backgroundColor: 'rgba(241, 85, 85, 0.15)',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '12px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 5px rgba(241, 85, 85, 0.2)'
                        }}>
                          <Icon.X color="#f15555" size={16} />
                        </div>
                      ) : dbt_test_status === "testing" || (!dbt_test_status && dbt_logs) ? (
                        <div style={{
                          backgroundColor: 'rgba(85, 110, 230, 0.1)',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '12px',
                          transition: 'all 0.2s ease'
                        }}>
                          <CircularProgress
                            size={18}
                            style={{
                              color: '#556ee6',
                              animation: 'rotate 1.5s linear infinite'
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            backgroundColor: 'rgba(85, 110, 230, 0.1)',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '12px'
                          }}
                        >
                          <Icon.Code size={16} color="#556ee6" />
                        </div>
                      )}

                      {dbt_test_status === "testing" || (!dbt_test_status && dbt_logs) ? (
                        <span
                          style={{
                            animation: 'pulseText 1.5s infinite ease-in-out',
                            display: 'inline-flex',
                            alignItems: 'center',
                            minWidth: '180px'
                          }}
                        >
                          Testing your branch...
                          <span
                            style={{
                              width: '18px',
                              textAlign: 'left',
                              display: 'inline-block',
                              animation: 'ellipsis 1.5s infinite steps(4, end)',
                              overflow: 'hidden'
                            }}
                          >
                            ...
                          </span>
                        </span>
                      ) : (
                        "DBT Test Logs"
                      )}
                    </div>
                  </Accordion.Header>
                  {dbt_logs && (
                    <Accordion.Body
                      className="animate__animated animate__fadeIn"
                      style={{
                        backgroundColor: 'rgba(85, 110, 230, 0.03)',
                        borderTop: '1px solid rgba(85, 110, 230, 0.05)',
                        borderRadius: '0 0 18px 18px',
                        transition: 'all 0.3s ease-in-out',
                      }}
                    >
                      <div
                        className="log-display"
                        dangerouslySetInnerHTML={{
                          __html: dbt_logs,
                        }}
                      />
                    </Accordion.Body>
                  )}
                </Accordion.Item>
              </Accordion>
            </Form>
            </div>

            {/* Fixed button at bottom */}
            <div style={{ padding: '15px 30px 30px', backgroundColor: isDarkMode ? '#1e1e1e' : 'transparent' }}>
              <Button
                variant="contained"
                onClick={handleCreatePullRequest}
                disabled={!selectedBranch}
                sx={{
                  borderRadius: "5px",
                  width: "100%",
                  backgroundColor: dbt_test_status === "failed" || dbt_test_status === "not_started" || dbt_test_status === "testing" || (!dbt_test_status && dbt_logs) ? "#dc3545" : "#54cb75",
                  padding: "12px 16px",
                  "&:hover": {
                    opacity: 0.8
                  },
                  "&:disabled": {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#ccc",
                    color: isDarkMode ? "#858585" : undefined
                  }
                }}
              >
              <span style={{ fontWeight: "800", letterSpacing: "0.3px", fontSize: "12px" }}>
                {dbt_test_status === "failed" || dbt_test_status === "not_started" || dbt_test_status === "testing" || (!dbt_test_status && dbt_logs) ? (
                  <div style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    position: "relative",
                    paddingRight: "35px"
                  }}>
                    <span>Bypass test & Create pull request</span>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      borderRadius: "50%",
                      padding: "5px",
                      height: "24px",
                      width: "24px",
                      position: "absolute",
                      right: "5px"
                    }}>
                      <Icon.GitPullRequest width={16} />
                    </div>
                  </div>
                ) : (
                  <>Create Pull Request <Icon.GitPullRequest width={24} className="button-icon" /></>
                )}
              </span>
            </Button>
            </div>
          </div>
        </section>
      </ResizableBox>

      <ByPassTestsModal
        isShown={showBypassModal}
        onHide={() => setShowBypassModal(false)}
        onAction={() => {
          setShowBypassModal(false);
          openGitHubPullRequest();
        }}
        isDarkMode={isDarkMode}
      />
    </>
  );
}
